import { ApiError } from '@boothiva/api-client';
import { useLogout, type TenantSummary } from '@boothiva/auth';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Skeleton, notify } from '@boothiva/ui';
import { useState } from 'react';
import { createCheckoutSession, listPlans } from './api';
import { PlanPicker } from './PlanPicker';

function errorMessage(error: unknown) { return error instanceof ApiError ? error.message : 'Something went wrong. Please try again.'; }

/** Rendered by RequireAuth's suspendedFallback in place of the normal app shell — this is the only
 * screen a Suspended tenant can reach, matched by TenantResolutionMiddleware's billing-only allowlist
 * (GET plans, POST checkout-session). Nothing else on the customer portal is reachable from here. */
export function SuspendedWorkspace({ tenant }: { tenant: TenantSummary }) {
  const [interval, setInterval_] = useState<'monthly' | 'yearly'>('monthly');
  // A manual suspension is not something the customer can pay their way out of, so the plan query is not
  // even issued -- the backend blocks it for them anyway, and asking would only surface an error toast.
  const billingRecoverable = tenant.suspensionKind === 'Billing';
  const plansQuery = useQuery({ queryKey: ['billing', 'plans'], queryFn: listPlans, enabled: billingRecoverable });
  const logout = useLogout();

  const checkoutMutation = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (value) => { window.location.assign(value.url); },
    onError: (error) => notify.error(errorMessage(error)),
  });

  return <main className="bv-form-stack" style={{ maxWidth: '64rem', margin: '0 auto', padding: '2rem 1rem' }}>
    <section className="bv-surface" aria-labelledby="suspended-heading">
      <h1 id="suspended-heading" className="bv-section-title">This workspace is suspended</h1>
      {billingRecoverable
        ? <p className="bv-muted">
            {tenant.businessName}'s subscription has ended, so {tenant.businessName} is currently locked
            except for choosing a plan below. Pick a plan to resubscribe and get back in.
          </p>
        // Deliberately says nothing about why. The operator's reason is an internal note, and it is not
        // sent to the browser at all -- only the kind is.
        : <p className="bv-muted">
            Access to {tenant.businessName} has been suspended. Please contact support at{' '}
            <a href="mailto:support@boothiva.com">support@boothiva.com</a> and we'll help sort it out.
          </p>}
    </section>
    {billingRecoverable ? <section className="bv-surface" aria-labelledby="plans-heading">
      <div className="bv-billing-plans__head">
        <h2 id="plans-heading" className="bv-card-title">Choose a plan</h2>
      </div>
      {plansQuery.isLoading ? <Skeleton height="10rem" label="Loading plans" /> : null}
      {plansQuery.isError ? <p role="alert">{errorMessage(plansQuery.error)}</p> : null}
      {plansQuery.data ? <PlanPicker
        plans={plansQuery.data}
        interval={interval}
        onIntervalChange={setInterval_}
        pending={checkoutMutation.isPending}
        onChoose={(plan, selectedInterval) => checkoutMutation.mutate({ planCode: plan.code, interval: selectedInterval })}
      /> : null}
    </section> : null}
    <button type="button" className="bv-button bv-button--secondary" onClick={() => logout()}>Sign out</button>
  </main>;
}
